import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AccentButton, NeutralButton } from '../../../shared/Buttons';
import { X } from '@phosphor-icons/react';

interface SkipPlaidModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onLinkPlaid: () => void;
}

function SkipPlaidModal({
  isOpen,
  onCancel,
  onConfirm,
  onLinkPlaid,
}: SkipPlaidModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onCancel]);

  // Handle click outside modal
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="relative max-w-[480px] rounded-lg bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="skip-plaid-modal-title"
      >
        {/* Close button */}
        <button
          onClick={onCancel}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
          aria-label="Close modal"
        >
          <X size={24} />
        </button>

        {/* Modal content */}
        <div className="space-y-4">
          <h2
            id="skip-plaid-modal-title"
            className="text-2xl font-semibold text-primary"
          >
            Submit bank statements instead?
          </h2>

          <p className="text-base text-gray-600">
            Linking with Plaid is instant and gets you approved fastest. If
            you'd rather not, we'll ask you to upload your last 6 months of
            business bank statements on the next step.
          </p>

          {/* Action buttons — steer toward Plaid (primary), statements secondary */}
          <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
            <NeutralButton
              onClick={onConfirm}
              addClass="w-full rounded-full sm:w-auto px-8 py-3"
            >
              Continue with statements
            </NeutralButton>
            <AccentButton
              onClick={onLinkPlaid}
              addClass="w-full rounded-full sm:w-auto px-8 py-3"
            >
              Link with Plaid
            </AccentButton>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at the root level
  return createPortal(modalContent, document.body);
}

export default SkipPlaidModal;
